import { useCallback, useEffect, useState } from 'react';
import { ACTOR_TYPES, ACTOR_TYPE_LABELS } from '@cuito/types';
import { Button, formatBeijingTime } from '@cuito/ui';
import { auditApi, type AuditLogView } from '../api/audit';

const PAGE_SIZE = 20;

// `ACTOR_TYPES` / `ACTOR_TYPE_LABELS` 来自 `@cuito/types`（`packages/types/src/audit.ts`）。
// 原先这里就地抄了一份白名单，因为当时枚举只在服务端；
// 两份会各自漂移（后端加主体类型、这里下拉框不跟着变），
// 而这类不一致不会让任何测试变红。权威校验仍在服务端 `@IsEnum(ACTOR_TYPES)`。

/**
 * 主体类型 → 中文标签。
 *
 * 入参是 `string`（来自 API）而不是 `ActorType`：服务端将来新增主体类型时，
 * 旧前端不应因此崩掉或显示 `undefined`，而是**回退显示原始值** ——
 * 让运维至少能看到「有个它不认识的主体类型」，比空单元格有用。
 */
function actorTypeLabel(value: string): string {
  return value in ACTOR_TYPE_LABELS
    ? ACTOR_TYPE_LABELS[value as keyof typeof ACTOR_TYPE_LABELS]
    : value;
}

/** 状态快照 → 单行摘要；空值用 `-`，避免表格里出现 `undefined` */
function summary(value: unknown): string {
  if (value === null || value === undefined) return '-';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/**
 * 审计日志（`05-ui-design-system.md` §3.2 的只读页）。
 *
 * **故意不渲染任何删除或编辑按钮**：契约 §24.3 规定日志只能由服务端在业务事务内
 * 追加，任何人（含平台管理员）都不能修改或删除。页脚那句话不是装饰，
 * 它是这张页面唯一正确的操作说明。
 */
export default function AuditLogsPage() {
  const [logs, setLogs] = useState<AuditLogView[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [action, setAction] = useState('');
  const [targetType, setTargetType] = useState('');
  const [actorType, setActorType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await auditApi.list({
        page,
        pageSize: PAGE_SIZE,
        ...(action.trim() === '' ? {} : { action: action.trim() }),
        ...(targetType.trim() === '' ? {} : { targetType: targetType.trim() }),
        ...(actorType === '' ? {} : { actorType }),
        // `datetime-local` 的本地时间转成契约 §1.6 要求的 ISO 8601 绝对时刻
        ...(from === '' ? {} : { from: new Date(from).toISOString() }),
        ...(to === '' ? {} : { to: new Date(to).toISOString() }),
      });
      setLogs(res.items);
      setTotal(res.total);
    } catch {
      setError('加载失败，请重试');
    } finally {
      setLoading(false);
    }
  }, [action, actorType, from, page, targetType, to]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold text-gray-900">审计日志</h1>

      <form
        className="mb-4 flex flex-wrap items-end gap-3 rounded-lg border-2 border-black p-4"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          void load();
        }}
      >
        <label className="flex flex-col gap-1">
          <span className="text-sm font-bold">动作</span>
          <input
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="如 stock.adjust"
            className="h-12 w-56 rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm font-bold">目标类型</span>
          <input
            value={targetType}
            onChange={(e) => setTargetType(e.target.value)}
            placeholder="如 StockItem"
            className="h-12 w-56 rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm font-bold">操作者类型</span>
          <select
            value={actorType}
            onChange={(e) => setActorType(e.target.value)}
            className="min-h-[44px] rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
          >
            <option value="">全部</option>
            {ACTOR_TYPES.map((value) => (
              <option key={value} value={value}>
                {ACTOR_TYPE_LABELS[value]}
              </option>
            ))}
          </select>
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm font-bold">开始时间</span>
          <input
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="h-12 rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
          />
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-sm font-bold">结束时间</span>
          <input
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="h-12 rounded-lg border-2 border-black bg-white px-3 text-base outline-none focus:ring-2 focus:ring-black"
          />
        </label>

        <Button type="submit" variant="secondary">
          查询
        </Button>
      </form>

      {loading && <p className="py-8 text-center text-gray-500">加载中…</p>}
      {!loading && error && <p className="font-bold text-red-600">{error}</p>}

      {!loading && !error && logs.length === 0 && (
        <p className="text-gray-500">暂无审计日志</p>
      )}

      {!loading && !error && logs.length > 0 && (
        <table className="w-full rounded-lg border-2 border-black">
          <thead className="bg-gray-50">
            <tr>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">时间</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">操作者类型</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">动作</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">目标</th>
              <th className="border-b-2 border-black px-4 py-3 text-left font-bold">前后值摘要</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <tr key={log.id}>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {formatBeijingTime(log.createdAt)}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {actorTypeLabel(log.actorType)}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">{log.action}</td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  {log.targetType}
                  {log.targetId && (
                    <span className="ml-2 text-xs text-gray-500">{log.targetId.slice(0, 8)}</span>
                  )}
                </td>
                <td className="border-b border-gray-200 px-4 py-3 text-sm">
                  <div
                    className="max-w-xs truncate text-xs text-gray-600"
                    title={`前：${summary(log.before)} 后：${summary(log.after)}`}
                  >
                    前：{summary(log.before)} 后：{summary(log.after)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!loading && !error && totalPages > 1 && (
        <div className="mt-4 flex items-center gap-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            上一页
          </Button>
          <span className="text-sm text-gray-600">
            第 {page} / {totalPages} 页
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            下一页
          </Button>
        </div>
      )}

      <p className="mt-8 text-sm text-gray-500">审计日志只读，不可修改或删除</p>
    </div>
  );
}
